// "Get Trade-In Value" CTA in the Summary section. Customers with an
// existing Gammill or competitor machine can request a trade-in
// valuation. We collect Brand/Model/Year/Serial/Hours/Condition +
// notes, POST to /api/tradein/submit which upserts the contact +
// attaches a "🔄 Configurator trade-in valuation request" note for
// the trade-in team.
//
// Name / email / phone come from the configurator's Details section
// (the button only enables AFTER Calculate, which requires those
// fields), so we don't re-ask for them. Per CLAUDE.md: serial number
// is the load-bearing field for Gammill trade-in valuation —
// required. Photos are deferred to follow-up (sales requests by
// email/text); modal stays light.

import { useState } from "react";

import { api } from "../api";
import { useConfigurator } from "../store";
import { ContactSummary, Field, FormModal, inputClass } from "./FormModal";

interface Props {
  open: boolean;
  onClose: () => void;
}

const CONDITIONS = [
  "Excellent — like new",
  "Good — used regularly, well-maintained",
  "Fair — visible wear, runs",
  "Poor — needs repair",
  "Not running",
];

export function TradeInModal({ open, onClose }: Props) {
  const { state } = useConfigurator();
  const c = state.customer;

  const [brand, setBrand] = useState("Gammill");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [serial, setSerial] = useState("");
  const [condition, setCondition] = useState(CONDITIONS[1]);
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    !submitting &&
    c.email.trim().length > 0 &&
    brand.trim() &&
    model.trim() &&
    // Serial is the load-bearing field per Gammill's trade-in process.
    serial.trim();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.submitTradeIn({
        customer: {
          first_name: c.firstName,
          last_name: c.lastName,
          email: c.email,
          phone: c.phone,
          address: c.address,
          city: c.city,
          state: c.state,
          zip: c.zip,
        },
        brand,
        model,
        year,
        serial,
        // hours field removed from the form per user request; pass
        // empty string so the backend's note rendering still works.
        hours: "",
        condition,
        notes,
      });
      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not submit — please try again or call us.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    setSubmitted(false);
    setError(null);
    onClose();
  }

  return (
    <FormModal
      open={open}
      onClose={handleClose}
      title="Get Trade-In Value"
      subtitle="Tell us about your existing machine. A Gammill specialist will follow up with a trade-in offer using your saved contact details."
    >
      {submitted ? (
        <div className="py-4">
          <p className="font-display text-lg font-extrabold uppercase tracking-wide text-ink">
            Thanks — we'll be in touch.
          </p>
          <p className="mt-2 font-serif text-base italic text-graphite">
            Your trade-in request is with our valuation team. A Gammill
            specialist will reach out to confirm details and (often)
            request a few photos before issuing a value.
          </p>
          <button
            type="button"
            onClick={handleClose}
            className="mt-4 inline-flex items-center justify-center rounded-full bg-amber px-5 py-2.5 font-sans text-sm font-bold tracking-[0.02em] text-ink shadow-[0_4px_12px_rgba(250,173,61,0.32)] transition hover:bg-amber-hover"
          >
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit}>
          <ContactSummary customer={c} />

          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand" required>
              <input
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className={inputClass}
                required
              />
            </Field>
            <Field label="Model" required>
              <input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className={inputClass}
                placeholder="e.g. Premier Plus, Statler 26"
                required
              />
            </Field>
          </div>
          <Field label="Year">
            <input
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className={inputClass}
              placeholder="e.g. 2015"
            />
          </Field>
          <Field
            label="Serial number"
            hint="On the machine plate. Required for valuation."
            required
          >
            <input
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              className={inputClass}
              required
            />
          </Field>
          <Field label="Condition">
            <select
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              className={inputClass}
            >
              {CONDITIONS.map((cond) => (
                <option key={cond} value={cond}>
                  {cond}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Notes (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className={inputClass}
              placeholder="Anything else our team should know?"
            />
          </Field>

          {error && (
            <p className="my-3 border-l-[3px] border-red-400 pl-4 font-sans text-sm text-red-700">
              {error}
            </p>
          )}

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2.5 font-sans text-sm font-semibold text-steel hover:text-ink"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center justify-center rounded-full bg-amber px-5 py-2.5 font-sans text-sm font-bold tracking-[0.02em] text-ink shadow-[0_4px_12px_rgba(250,173,61,0.32)] transition hover:bg-amber-hover disabled:cursor-not-allowed disabled:bg-mist disabled:text-steel disabled:shadow-none"
            >
              {submitting ? "Submitting…" : "Submit"}
            </button>
          </div>
        </form>
      )}
    </FormModal>
  );
}
